import {
    ClockIcon,
    DocumentCheckIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StatCard } from './StatusCards';

// Clickable status stat cards above the charges table. Counts come from the
// list endpoint's statusCounts meta, which respects the practice/search
// filters but not the status filter, so the numbers stay stable while a
// card is selected.

interface CardConfig {
    status: string | null; // null = all charges
    label: string;
    unit: string;
    icon: React.ReactNode;
}

const ICON_CLASS = 'h-3.5 w-3.5';

const CARD_CONFIGS: CardConfig[] = [
    {
        status: null,
        label: 'All charges',
        unit: 'total',
        icon: <Squares2X2Icon className={ICON_CLASS} />
    },
    {
        status: 'pending',
        label: 'Pending',
        unit: 'to process',
        icon: <ClockIcon className={ICON_CLASS} />
    },
    {
        status: 'invoiced',
        label: 'Invoiced',
        unit: 'billed',
        icon: <DocumentCheckIcon className={ICON_CLASS} />
    },
    {
        status: 'error',
        label: 'Error',
        unit: 'failed',
        icon: <ExclamationTriangleIcon className={ICON_CLASS} />
    },
    {
        status: 'canceled',
        label: 'Canceled',
        unit: 'charges',
        icon: <NoSymbolIcon className={ICON_CLASS} />
    }
];

interface ChargeStatusCardsProps {
    statusCounts: Record<string, number>;
    selected: string[];
    onChange: (next: string[]) => void;
}

export function ChargeStatusCards({ statusCounts, selected, onChange }: ChargeStatusCardsProps) {
    const allCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);

    return (
        <div className="flex items-stretch gap-3 px-8 pt-4">
            {CARD_CONFIGS.map((config) => {
                const isActive =
                    config.status === null
                        ? selected.length === 0
                        : selected.length === 1 && selected[0] === config.status;
                const count =
                    config.status === null ? allCount : (statusCounts[config.status] ?? 0);
                return (
                    <StatCard
                        key={config.status ?? 'all'}
                        label={config.label}
                        icon={config.icon}
                        value={String(count)}
                        unit={config.unit}
                        isActive={isActive}
                        onClick={() => {
                            if (config.status === null || isActive) {
                                onChange([]);
                            } else {
                                onChange([config.status]);
                            }
                        }}
                    />
                );
            })}
        </div>
    );
}
